import React from 'react';
import { WithStyles, withStyles, createStyles } from '@material-ui/core/styles';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { Dialog, Theme, Typography } from '@material-ui/core';
import { ButtonProps } from '@material-ui/core/Button';

// @ts-ignore
import CircularIndeterminate from 'hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate';

import ActionBarMenuButton from './ActionBar/ActionBarButton';
import HclApiManager from '../../../../../Api/HclApiManager';
import QuantificationResultModal from '../Quantification/QuantificationResultModal';
import BayesianNetworkResultView from '../../../QuantificationResult/BayesianNetworkResultView';
import { TreeNames } from './TreeDictionaries';
import ImportanceMeasureView from '../../../QuantificationResult/ImportanceMeasures/ImportanceMeasureView';
import { Configurations } from './Quantification';

import { FullScreenConsumer } from '../../../../Primitives/FullScreenProvider';
import { QuantificationResultJSON } from '../../../Quantification/QuantificationResultJSON';
import { TreeContext } from '../../TreeContext';
import CutsetView from '../../../QuantificationResult/CutsetView';

const styles = (theme: Theme) => createStyles({
  progress: {
    margin: '6px',
  },
  quantify: {
    marginLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
  },
  icon: {
    width: '16rem',
    height: '16rem',
    margin: 'auto',
    color: theme.palette.text.secondary,
  },
});

export interface QuantificationResultResponse {
  configuration?: Configurations;
  creator?: number;
  date_created?: string;
  id?: number;
  model?: number;
  model_title?: string;
  result_data?: {[index: string]: any};
  importance?: {[index: string]: any};
}

interface QButtonProps extends ButtonProps {
  classOverride: WithStyles<typeof styles>;
}

interface Props extends WithStyles<typeof styles>, InjectedIntlProps {
  disabled: boolean;
  modelId: number;
  treeId: number;
  treeType: TreeNames;
  settingsInLocalStorage: boolean;
  checkQuantificationSettings(): void;
  setConfig?: () => void;
  buttonProps: QButtonProps;
  quantifyOverride?: () => void;
}

interface State {
  modalOpen: boolean;
  quantificationResult: QuantificationResultJSON;
  quantifying: boolean;
}

class QuantifyTreeButton extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      quantificationResult: null,
      quantifying: false,
    };
  }

  private quantifyTree = () => {
    const { modelId, treeId, quantifyOverride } = this.props;
    this.setState({
      quantifying: true,
    }, () => {
      const configs = JSON.parse(localStorage.getItem('configurations'));
      const currentName = configs.currentlySelected;
      const currentConfig = configs.configurations[currentName];
      if (quantifyOverride !== null && quantifyOverride !== undefined) {
        quantifyOverride();
        this.setState({ quantifying: false });
      } else {
        // @ts-ignore
        HclApiManager.quantifyHCLTreeWithSettings(modelId, Number(treeId), currentConfig, {
            successText: 'Quantification Successful',
            failText: 'Quantification Failed',
          },
          // @ts-ignore
          res => res.status === 201 && res.json()
            .then((data: QuantificationResultJSON) => {
              this.setState(
                {
                  modalOpen: true,
                  quantificationResult: data,
                  quantifying: false,
                },
              );
            }),
          () => this.setState(
            {
              modalOpen: false,
              quantificationResult: null,
              quantifying: false,
            },
          ));
      }
    });
  };

  private getCutsetView = () => {
    const { treeType } = this.props;
    const { quantificationResult, quantifying, quantificationResult: { result_data, configuration: { quantify: { type } } } } = this.state;
    switch (treeType) {
      case TreeNames.FAULT_TREE: return <TreeContext.Consumer>{value => <CutsetView resultData={result_data} type={type} treeTitle={value.detailsWithMetaData.title} basicEvents={value.detailsWithMetaData.tree_data.basic_events} />}</TreeContext.Consumer>;
      case TreeNames.EVENT_TREE: return <TreeContext.Consumer>{value => <CutsetView resultData={result_data} type={type} treeTitle={value.detailsWithMetaData.title} riskCurves={value.riskCurves} />}</TreeContext.Consumer>
      case TreeNames.BAYESIAN_NETWORK: return <BayesianNetworkResultView noSlide loading={quantifying} data={quantificationResult} type={type} />;
      default: return JSON.stringify(quantificationResult);
    }
  };

  private getImportanceView = () => {
    const { classes } = this.props;
    const { quantificationResult, quantifying } = this.state;
    if (quantificationResult.result_data && quantificationResult.result_data.importance) {
      // @ts-ignore
      return (<ImportanceMeasureView noSlide loading={quantifying} data={quantificationResult} map={quantificationResult.result_data.label_map} treeType />);
    }
    return (
      <div className={classes.root}>
        <Typography variant="h5" align="center" color="textSecondary" paragraph>
          No Importance Measures Specified
        </Typography>
      </div>
    );
  };

  private handleClose = (e: React.SyntheticEvent) => {
    this.setState({ modalOpen: false });
  };

  render() {
    const {
      classes, disabled, buttonProps, setConfig,
    } = this.props;

    const { modalOpen, quantifying, quantificationResult } = this.state;

    return (
      <React.Fragment>
        <FullScreenConsumer>
          {({ isFull }) => (
            <Dialog
              maxWidth={false}
              open={modalOpen}
              fullWidth
              scroll="paper"
              onClose={this.handleClose}
              disablePortal={isFull}
            >
              {quantificationResult && (
                <TreeContext.Consumer>
                  {({ detailsWithMetaData: { title }, riskCurves, lineLevelRiskCurves}) => (
                    <QuantificationResultModal
                      treeTitle={title}
                      cutsetView={this.getCutsetView()}
                      measureView={this.getImportanceView()}
                      detail={quantificationResult}
                      requestClose={this.handleClose}
                      riskCurves={riskCurves}
                      lineLevelRiskCurves={lineLevelRiskCurves}
                    />
                  )}
                </TreeContext.Consumer>
              )}
            </Dialog>
          )}
        </FullScreenConsumer>
        <ActionBarMenuButton
          onClick={() => {
            if (setConfig) {
              setConfig();
            }
            this.quantifyTree();
          }}
          variant="contained"
          color="secondary"
          disabled={disabled || quantifying}
          {...buttonProps}
        >
          {quantifying && <CircularIndeterminate size={12} color="secondary" className={classes.progress} />}
          {quantifying ? 'QUANTIFYING' : 'QUANTIFY'}
        </ActionBarMenuButton>
      </React.Fragment>
    );
  }
}

// @ts-ignore
export default injectIntl(withStyles(styles)((QuantifyTreeButton)));
